import React, { FC, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { createGlobalState } from 'react-use';
import { useLocation } from 'react-router-dom';
import pageViewedAnalytics, {
  getPageNameByPath,
  isPathContentDetails,
  isPathLiveTv,
  PageName,
  PageViewedAnalyticsProps,
} from '@/Analytics/Page';
import { RoutePath } from '@/Routes/RoutePath';
import { getUnifiedSessionId } from '@/Providers/BrowserSessionProvider';
import sessionId from '@/Utils/SessionId';

interface PageHeader {
  title?: string;
  description?: string;
}
const PROD_SEO_DOMAIN = 'https://www.skygo.co.nz';
export const DEFAULT_TITLE = 'Sky Go';
export const DEFAULT_DESCRIPTION =
  'Watch Sky online with Sky Go. Live sports, news, box sets, catch up movies, shows and entertainment. For Sky TV customers, anywhere in New Zealand.';

const useHeader = createGlobalState<PageHeader>({});

export const usePageHeader = (pageHeader: PageHeader) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setPageHeader] = useHeader();
  const { title, description } = pageHeader;

  useEffect(() => {
    setPageHeader({ title: title ? `${title} | ${DEFAULT_TITLE}` : undefined, description });
    return () => {
      setPageHeader({});
    };
    // we need to add the separated dependency for title and description here,
    // to avoid the cases when different object passed with the same value.
  }, [title, description, setPageHeader]);
};

interface PageTrackAnalyticsProps {
  pageName: string;
  payload: PageViewedAnalyticsProps;
}
const AppHeader: FC = () => {
  const prevLocationRef = useRef('');
  const pageTrackAnalyticsRef = useRef<PageTrackAnalyticsProps>();
  const location = useLocation();
  const [pageHeader] = useHeader();
  const title = pageHeader?.title || DEFAULT_TITLE;
  const description = pageHeader?.description || DEFAULT_DESCRIPTION;

  // pageViewedAnalytics on location change
  useEffect(() => {
    const docReferrerOrigin = document.referrer
      ? new URL(document.referrer).origin
      : window.location.origin;
    const referrer =
      docReferrerOrigin !== window.location.origin
        ? document.referrer
        : `${window.location.origin}${prevLocationRef.current}`;
    // If path does not include channel id or it was already stored previously,
    // don't fire page event
    if (
      location.pathname === RoutePath.myHubLanding ||
      location.pathname === '/watch-tv' ||
      location.pathname === prevLocationRef.current
    )
      return;

    let pageName = getPageNameByPath(location.pathname);
    if (PageName.NotFound === pageName && location.state?.pageName) {
      pageName = location.state?.pageName;
    }
    let pageViewPayload: PageViewedAnalyticsProps = {
      name: pageName,
      path: location.pathname,
      referrer,
      search: location.search,
      title: `Sky Go`,
      url: `${window.location.origin}${location.pathname}`, // with domain
    };

    if (location.pathname === RoutePath.myHubManageDevices) {
      pageViewPayload = {
        ...pageViewPayload,
        session_id: getUnifiedSessionId() ?? sessionId,
      };
    }
    // Fire pageViewedAnalytics when pageHeader does not change. NOT /show, /movie, /watch-tv
    if (isPathContentDetails(location.pathname) || isPathLiveTv(location.pathname)) {
      pageTrackAnalyticsRef.current = { pageName, payload: pageViewPayload };
    } else {
      pageViewedAnalytics(pageName, pageViewPayload);
      pageTrackAnalyticsRef.current = undefined;
    }
    prevLocationRef.current = location.pathname;
  }, [location]);

  // Fire pageViewedAnalytics when pageHeader changes for /show, /movie, /watch-tv
  useEffect(() => {
    if (!pageHeader?.title || !pageTrackAnalyticsRef.current?.pageName) return;
    const { pageName, payload } = pageTrackAnalyticsRef.current;

    // Don't re-send LiveTV page event when user opens content drawer and switch channels
    const referrerPath = payload.referrer ? new URL(payload.referrer).pathname : '';
    const referrerIsWatchTv = isPathLiveTv(referrerPath);
    const currentPathIsWatchTv = isPathLiveTv(payload.path);
    if (pageName === PageName.LiveTv && referrerIsWatchTv === currentPathIsWatchTv) return;

    pageViewedAnalytics(pageName, {
      ...payload,
      title: pageHeader.title,
    });
    pageTrackAnalyticsRef.current = undefined;
  }, [pageHeader]);

  // we have other domains for PROD which are used for Akamai, need to hide them from SEO
  // https://developers.google.com/search/docs/advanced/crawling/consolidate-duplicate-urls
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={`${PROD_SEO_DOMAIN}${location.pathname}${location.search}`} />
    </Helmet>
  );
};

export default AppHeader;
